import type { CategoryPageData } from './types'

import WhyImg2 from '@/assets/images/refresh/categories/why-gm/why-1.webp'
import WhyImg1 from '@/assets/images/refresh/categories/why-gm/why-2.webp'

import MedicationsHeroLoop from '@/assets/images/refresh/categories/medications/medications-how-it-works-loop.mp4'
import Step1CarePlan from '@/assets/images/refresh/categories/medications/how-it-works/step-1-care-plan.png'
import Step2ChoosePharmacy from '@/assets/images/refresh/categories/medications/how-it-works/step-2-choose-pharmacy.png'
import Step3OnItsWay from '@/assets/images/refresh/categories/medications/how-it-works/step-3-on-its-way.png'
import Step4Reorder from '@/assets/images/refresh/categories/medications/how-it-works/step-4-reorder.png'
import { PRESCRIPTION_RENEWAL_HREF } from '@/types/enums/sku-constants'

// Key order follows renderedServices.
export const medicationsData: CategoryPageData = {
    // medications_hero — type: categoryHeaderWithSearch
    categoryHeaderWithSearch: {
        title: 'Your medications,\non your terms.',
        description:
            'Compare prices across pharmacies before you choose where to fill. Reorder in a few of taps, and switch pharmacies anytime — you\u2019re never locked into wherever your prescription first landed.',
        searchPlaceholder: 'Search medications...',
        searchFilter: 'medications',
        heroVideo: {
            loopVideoSrc: MedicationsHeroLoop,
            playVideoUrl:
                'https://player.vimeo.com/progressive_redirect/playback/1227044381/rendition/1080p/file.mp4%20%281080p%29.mp4?loc=external&signature=0ceecce9f734e0ab5b111248dad5cc612b79e978f7a0596d0851021f28ba3e70',
            playButtonLabel: 'How it works (0:33)',
        },
        images: [],
    },

    generalAISm: {
        title: 'Not sure where to start? Let General AI help.',
        body: 'Curious whether this medication is the right fit, or if there\u2019s a lower-cost alternative? Chat with General AI to explore your options, understand treatment paths, and get ready for a more productive visit with your clinician.',
    },

    // medications_how_it_works — type: howItWorks
    howItWorks: {
        steps: [
            {
                order: 1,
                title: 'Get prescriptions, fast',
                description:
                    'Our clinical team reviews your health history and handles prescriptions quickly — often in a single visit, with no back-and-forth. We even take care of prior authorizations.',
                image: {
                    src: Step1CarePlan,
                    alt: 'General Medicine app showing a care plan with a prescription ready to order',
                },
            },
            {
                order: 2,
                title: 'Know what you\u2019ll pay',
                description:
                    'Search for your medication and compare prices — both cash and insurance — across pharmacy locations.',
                image: {
                    src: Step2ChoosePharmacy,
                    alt: 'General Medicine app comparing pharmacy prices for a medication',
                },
            },
            {
                order: 3,
                title: 'Pay ahead, fill anywhere',
                description:
                    'Pick wherever works for you: your corner CVS, a nearby Walgreens, or mail order. At retail pharmacies, you can checkout with us and skip paying at the counter.',
                image: {
                    src: Step3OnItsWay,
                    alt: 'General Medicine app confirming a medication order on its way to a pharmacy',
                },
            },
            {
                order: 4,
                title: 'Get hassle-free refills',
                description:
                    'When it\u2019s time to refill a medication, we\u2019ll notify you before you\u2019ve even thought about it — and you can reorder in seconds.',
                image: {
                    src: Step4Reorder,
                    alt: 'General Medicine app reorder medication screen',
                },
            },
        ],
    },

    // medications_search — type: categorySearch
    categorySearch: {
        title: 'Put your prescriptions\non easy mode',
        searchPlaceholder: 'Search medications...',
        searchFilter: 'medications',
    },

    // product-services — tabbedCardSlider
    tabbedCardSlider: {
        tabsAlignment: 'center',
    },

    // medications_fit — type: contentWithBulletList
    contentWithBulletList: {
        title: 'Is General Medicine right for your medication?',
        description:
            'If you prefer flexibility and are seeking a smoother healthcare experience, then try moving your prescription to General Medicine.',
        primaryCta: {
            label: 'Renew my prescription',
            href: PRESCRIPTION_RENEWAL_HREF,
        },
        items: [
            { title: 'You want to compare prices and make more informed decisions' },
            { title: 'You want to access your prescription wherever you go' },
            { title: 'You want someone to see your pre-authorization through' },
            { title: 'You want your doctor to have line of sight into what\u2019s happening at the Pharmacy' },
            { title: 'You don\u2019t want cost to be a reason to skip a fill' },
            { title: 'You want someone to do the remembering and the legwork for you' },
        ],
    },

    // why_choose_gm — widget / contentAccordion
    contentAccordion: {
        title: 'All your care, handled.',
        subtitle:
            '<p>We help you understand your options and get the right care for you. And your history travels with you, so nothing gets lost between one conversation and the next. No matter how big, small, or mysterious your health needs are, we\u2019ve got you.</p><ul><li>A complete view of your health history</li><li>Coordinated care \u2013 from specialists to labs to medication</li><li>Proactive next steps, so nothing falls through the cracks</li><li>Clear pricing options, with or without insurance</li></ul>',
        images: [WhyImg2, WhyImg1],
        accordionItems: [],
        primaryCta: {
            label: 'Get started',
            href: '/s/new-medication-consult/200011/screen',
        },
    },

    // get_started — widget / getStartedCta
    getStartedCta: {
        heading: 'Whether you\u2019re starting a new medication or switching over, we\u2019ve got you.',
        primaryCta: {
            label: 'Renew your prescription',
            href: PRESCRIPTION_RENEWAL_HREF,
        },
        secondaryCta: {
            label: 'Discuss a new medication',
            href: '/s/new-medication-consult/200011/screen',
        },
    },

    // faq — widget / faq
    faq: {
        title: 'Frequently asked questions',
        items: [
            {
                question: 'Do I need an existing prescription?',
                answer: 'No \u2014 you don\u2019t need one already. A clinician can meet with you, talk through what you need, and prescribe when it\u2019s clinically appropriate. If you do have an existing prescription, you can bring it in and manage it here too \u2014 compare pharmacy prices, refill it, or switch where it\u2019s filled.',
            },
            {
                question: 'How does pricing work? Will it be cheaper than my current pharmacy?',
                answer: 'You\u2019ll see prices across pharmacies before you decide where to fill, so you can compare cash and insurance costs side by side. It depends on your medication, insurance, and location \u2014 we show you the options, but we can\u2019t promise it\u2019ll always beat your current pharmacy.',
            },
            {
                question: 'Can I fill at any pharmacy, or only specific ones?',
                answer: 'Any pharmacy \u2014 your corner CVS, a Walgreens, mail order, wherever works for you. You\u2019re not locked into one choice; switch pharmacies anytime.',
            },
            {
                question: 'How do automatic refills work? Will I be charged without knowing?',
                answer: 'No \u2014 ordering refills is as easy as a few taps, but that\u2019s always your step to take. We\u2019ll notify you when it\u2019s time to order and you\u2019ll always have the option to change your payment method or pharmacy before placing the order. And if you choose not to continue refills, that\u2019s your choice to make as well.',
            },
            {
                question: 'What happens if I need a renewal and my prescription has run out of refills?',
                answer: 'A clinician reviews your history and renews it when it\u2019s clinically appropriate \u2014 you won\u2019t need to start over. Where possible we\u2019ll try to avoid unnecessary visits, so we may ask you to fill out a questionnaire or get labs before we add more refills. Some medications do require visits for renewals. No matter what\u2019s needed, we\u2019ll reach out well in advance with clear next steps \u2014 so you\u2019re not left without your medication.',
            },
            {
                question: 'Are there any medications you can\u2019t fill?',
                answer: 'Yes \u2014 we can\u2019t fill controlled substances, and what else is available can vary by state, since prescribing laws differ by location. We\u2019ll always be upfront about what we can and can\u2019t prescribe for you. And if the right medication for you is something we can\u2019t provide, we\u2019ll help connect you with someone who can.',
            },
        ],
    },

    // text_our_care_team — widget / support
    supportWidget: {
        hideEyebrow: true,
        titleOverride: 'More from General Medicine',
        itemIds: ['lab-tests', 'dermatology', 'flu-medication', 'everyday-care'],
    },
}
